/**
 * Sneak-style mix-and-match bundle builder.
 * Defines <ys-bundle-builder>, used only by sections/ys-bundle-builder.liquid
 * on templates/product.ys-01.json. Reuses window.routes, fetchConfig() (assets/global.js)
 * and the existing cart-notification/cart-drawer for the actual cart add + render.
 */
(function () {
  if (customElements.get('ys-bundle-builder')) return;

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (char) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }

  // Tokens use single braces ({amount}, {count}, ...) deliberately — Shopify's schema
  // editor treats {{ }} in setting defaults as an attempted Dynamic Source binding.
  function applyTokens(template, tokens) {
    return Object.keys(tokens).reduce(function (str, key) {
      return str.replace(new RegExp('\\{\\s*' + key + '\\s*\\}', 'g'), tokens[key]);
    }, template || '');
  }

  // Mirrors Shopify's standard money_format placeholder substitution.
  function applyMoneyFormat(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '{{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimalSep) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = typeof thousands === 'undefined' ? ',' : thousands;
      decimalSep = typeof decimalSep === 'undefined' ? '.' : decimalSep;
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var decimalPart = parts[1] ? decimalSep + parts[1] : '';
      return dollars + decimalPart;
    }

    var match = formatString.match(placeholderRegex);
    var key = match ? match[1] : 'amount';
    var value;

    switch (key) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount':
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return match ? formatString.replace(placeholderRegex, value) : formatString + value;
  }

  class YsBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const payloadScript = this.querySelector('script[data-ys-bb-payload]');
      if (!payloadScript) return;

      try {
        this.payload = JSON.parse(payloadScript.textContent);
      } catch (e) {
        console.error('ys-bundle-builder: invalid payload', e);
        return;
      }

      this.state = {
        tier: this.payload.defaultTier,
        selections: [],
        openPanelProductId: null,
        submitting: false,
        errorMessage: null,
        successFlash: false,
      };

      this.zones = {
        offers: this.querySelector('[data-ys-bb-offers]'),
        pill: this.querySelector('[data-ys-bb-pill]'),
        step2Heading: this.querySelector('[data-ys-bb-step2-heading]'),
        colorways: this.querySelector('[data-ys-bb-colorways]'),
        sizePanel: this.querySelector('[data-ys-bb-size-panel]'),
        rows: this.querySelector('[data-ys-bb-rows]'),
        upsell: this.querySelector('[data-ys-bb-upsell]'),
        button: this.querySelector('[data-ys-bb-button]'),
        error: this.querySelector('[data-ys-bb-error]'),
      };

      this._handleClick = this.handleClick.bind(this);
      this.addEventListener('click', this._handleClick);

      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._handleClick);
    }

    // ---- derived helpers ----

    totalSelectedQty() {
      return this.state.selections.reduce((sum, s) => sum + s.quantity, 0);
    }

    capForTier(tier) {
      return this.payload.tiers[String(tier)].pairs;
    }

    isComplete() {
      return this.totalSelectedQty() === this.capForTier(this.state.tier);
    }

    itemCompareContribution(item) {
      return item.compareAtPriceCents != null && item.compareAtPriceCents > item.priceCents
        ? item.compareAtPriceCents
        : item.priceCents;
    }

    crossedTotalCents(tierKey) {
      const tier = this.payload.tiers[String(tierKey)];
      if (String(tierKey) !== String(this.state.tier) || this.state.selections.length === 0) {
        if (this.payload.lowestCompareAtCents == null) return null;
        return this.payload.lowestCompareAtCents * tier.pairs;
      }
      return this.state.selections.reduce(
        (sum, item) => sum + this.itemCompareContribution(item) * item.quantity,
        0
      );
    }

    savingsCents(tierKey) {
      const crossed = this.crossedTotalCents(tierKey);
      if (crossed == null) return null;
      const packTotal = this.payload.tiers[String(tierKey)].packTotalCents;
      const savings = crossed - packTotal;
      return savings > 0 ? savings : null;
    }

    formatMoney(cents) {
      return applyMoneyFormat(cents, this.payload.moneyFormat);
    }

    // ---- state mutations ----

    normalizeState() {
      if (this.totalSelectedQty() >= this.capForTier(this.state.tier)) {
        this.state.openPanelProductId = null;
      }
    }

    setTier(newTier) {
      newTier = Number(newTier);
      const newCap = this.capForTier(newTier);
      const total = this.totalSelectedQty();
      if (newCap < total) {
        let excess = total - newCap;
        for (let i = this.state.selections.length - 1; i >= 0 && excess > 0; i--) {
          const item = this.state.selections[i];
          const reduceBy = Math.min(item.quantity, excess);
          item.quantity -= reduceBy;
          excess -= reduceBy;
          if (item.quantity === 0) this.state.selections.splice(i, 1);
        }
      }
      this.state.tier = newTier;
      this.state.openPanelProductId = null;
      this.render();
    }

    selectVariant(product, variant) {
      if (this.totalSelectedQty() >= this.capForTier(this.state.tier)) return;
      const variantId = String(variant.id);
      const existing = this.state.selections.find((s) => s.variantId === variantId);
      if (existing) {
        existing.quantity += 1;
      } else {
        this.state.selections.push({
          variantId,
          productId: String(product.productId),
          colourLabel: product.label,
          size: variant.size,
          image: variant.image || product.image,
          priceCents: variant.price,
          compareAtPriceCents: variant.compareAtPrice,
          quantity: 1,
        });
      }
      this.state.openPanelProductId = null;
      this.render();
    }

    increment(variantId) {
      if (this.totalSelectedQty() >= this.capForTier(this.state.tier)) return;
      const item = this.state.selections.find((s) => s.variantId === variantId);
      if (item) item.quantity += 1;
      this.render();
    }

    decrement(variantId) {
      const index = this.state.selections.findIndex((s) => s.variantId === variantId);
      if (index === -1) return;
      const item = this.state.selections[index];
      if (item.quantity > 1) item.quantity -= 1;
      else this.state.selections.splice(index, 1);
      this.render();
    }

    togglePanel(productId) {
      this.state.openPanelProductId = this.state.openPanelProductId === productId ? null : productId;
      this.render();
    }

    // ---- event delegation ----

    handleClick(event) {
      const target = event.target.closest('[data-action]');
      if (!target || !this.contains(target)) return;
      const action = target.dataset.action;

      if (action === 'select-tier') {
        this.setTier(target.dataset.tier);
      } else if (action === 'toggle-panel') {
        event.preventDefault();
        if (target.getAttribute('aria-disabled') === 'true') return;
        const productId = target.dataset.productId;
        this.togglePanel(productId);
        if (this.state.openPanelProductId === productId) {
          const firstBtn = this.zones.sizePanel.querySelector('.ys-bb__size-btn');
          if (firstBtn) firstBtn.focus();
        }
      } else if (action === 'close-panel') {
        const productId = this.state.openPanelProductId;
        this.state.openPanelProductId = null;
        this.render();
        const triggerBtn = this.zones.colorways.querySelector(`[data-ys-bb-colorway-btn="${productId}"]`);
        if (triggerBtn) triggerBtn.focus();
      } else if (action === 'select-size') {
        const product = this.payload.products.find(
          (p) => String(p.productId) === target.dataset.productId
        );
        const variant =
          product && product.variants.find((v) => String(v.id) === target.dataset.variantId);
        if (product && variant) {
          const productId = target.dataset.productId;
          this.selectVariant(product, variant);
          // selectVariant() re-renders the colorways zone, so the pre-click trigger
          // element is now detached — re-query the fresh one before focusing it.
          const triggerBtn = this.zones.colorways.querySelector(`[data-ys-bb-colorway-btn="${productId}"]`);
          if (triggerBtn) triggerBtn.focus();
        }
      } else if (action === 'increment') {
        this.increment(target.dataset.variantId);
      } else if (action === 'decrement') {
        this.decrement(target.dataset.variantId);
      } else if (action === 'upsell') {
        this.setTier(Number(this.state.tier) + 1);
        const heading = this.querySelector('[data-ys-bb-step2-heading]');
        if (heading) heading.focus();
      } else if (action === 'submit') {
        this.onSubmit();
      }
    }

    // ---- render ----

    render() {
      this.normalizeState();
      this.renderOffers();
      this.renderPill();
      this.renderStep2Heading();
      this.renderColorways();
      this.renderSizePanel();
      this.renderRows();
      this.renderUpsell();
      this.renderButton();
      this.renderError();
    }

    renderOffers() {
      const html = [1, 2, 3]
        .map((tierKey) => {
          const tier = this.payload.tiers[String(tierKey)];
          const selected = Number(this.state.tier) === tierKey;
          const crossed = this.crossedTotalCents(tierKey);
          const savings = this.savingsCents(tierKey);
          const savingsHtml =
            savings != null
              ? `<span class="ys-bb__savings">${escapeHtml(
                  applyTokens(this.payload.copy.savingsLabel, { amount: this.formatMoney(savings) })
                )}</span>`
              : '';

          return `
            <button type="button" class="ys-bb__offer-card" data-action="select-tier" data-tier="${tierKey}" aria-pressed="${selected}">
              ${tier.badge ? `<span class="ys-bb__badge">${escapeHtml(tier.badge)}</span>` : ''}
              <span class="ys-bb__offer-pairs">${tierKey} ${tierKey === 1 ? 'pereche' : 'perechi'}</span>
              <span class="ys-bb__offer-unit-price">${this.formatMoney(tier.pricePerPairCents)}</span>
              <span class="ys-bb__offer-unit-suffix">/ pereche</span>
              <span class="ys-bb__offer-total-group">
                <span class="ys-bb__offer-total-price">${this.formatMoney(tier.packTotalCents)}</span>
                ${crossed != null ? `<s class="ys-bb__offer-compare">${this.formatMoney(crossed)}</s>` : ''}
              </span>
              ${savingsHtml}
            </button>
          `;
        })
        .join('');
      this.zones.offers.innerHTML = html;
    }

    renderPill() {
      const total = this.totalSelectedQty();
      const cap = this.capForTier(this.state.tier);
      const chooseTemplate = cap === 1 ? this.payload.copy.pillChooseSingular : this.payload.copy.pillChoose;
      const text =
        total === 0
          ? applyTokens(chooseTemplate, { count: cap })
          : applyTokens(this.payload.copy.pillProgress, { selected: total, total: cap });
      this.zones.pill.textContent = text;
    }

    renderStep2Heading() {
      const cap = this.capForTier(this.state.tier);
      const heading = cap === 1 ? this.payload.copy.step2HeadingSingular : this.payload.copy.step2Heading;
      this.zones.step2Heading.textContent = heading;
    }

    renderColorways() {
      const cap = this.capForTier(this.state.tier);
      const total = this.totalSelectedQty();
      const atCap = total >= cap;

      const html = this.payload.products
        .map((product) => {
          const isOpen = this.state.openPanelProductId === String(product.productId);

          return `
            <button type="button" class="ys-bb__colorway" data-action="toggle-panel" data-product-id="${product.productId}" data-ys-bb-colorway-btn="${product.productId}" aria-expanded="${isOpen}" aria-disabled="${atCap}" aria-controls="ys-bb-size-panel">
              <img class="ys-bb__colorway-image" src="${product.image}" alt="${escapeHtml(product.label)}" loading="lazy" width="120" height="120">
              <span class="ys-bb__colorway-label">${escapeHtml(product.label)}</span>
            </button>
          `;
        })
        .join('');

      this.zones.colorways.innerHTML = html;
    }

    renderSizePanel() {
      const productId = this.state.openPanelProductId;
      if (!productId) {
        this.zones.sizePanel.innerHTML = '';
        this.zones.sizePanel.hidden = true;
        return;
      }

      const product = this.payload.products.find((p) => String(p.productId) === productId);
      if (!product) {
        this.zones.sizePanel.innerHTML = '';
        this.zones.sizePanel.hidden = true;
        return;
      }

      const availableVariants = product.variants.filter((v) => v.available);
      const sizeButtons = availableVariants
        .map(
          (v) => `
            <button type="button" class="ys-bb__size-btn" data-action="select-size" data-product-id="${product.productId}" data-variant-id="${v.id}">
              ${escapeHtml(v.size)}
            </button>
          `
        )
        .join('');

      const hintHtml =
        availableVariants.length && this.payload.copy.sizeHint
          ? `<p class="ys-bb__size-panel-hint">${escapeHtml(this.payload.copy.sizeHint)}</p>`
          : '';

      this.zones.sizePanel.hidden = false;
      this.zones.sizePanel.innerHTML = `
        <div class="ys-bb__size-panel-header">
          <p class="ys-bb__size-panel-title">Alege mărimea · ${escapeHtml(product.label)}</p>
          <button type="button" class="ys-bb__size-panel-close" data-action="close-panel" aria-label="Închide selectorul de mărimi">&times;</button>
        </div>
        ${hintHtml}
        <div class="ys-bb__size-panel-sizes">
          ${availableVariants.length ? sizeButtons : '<p class="ys-bb__no-sizes">Indisponibil momentan</p>'}
        </div>
      `;
    }

    renderRows() {
      if (this.state.selections.length === 0) {
        this.zones.rows.innerHTML = '';
        return;
      }
      const cap = this.capForTier(this.state.tier);
      const total = this.totalSelectedQty();

      const html = this.state.selections
        .map((item) => {
          const label = escapeHtml(item.colourLabel);
          const size = escapeHtml(item.size);
          return `
            <div class="ys-bb__row" data-ys-bb-row="${item.variantId}">
              <img class="ys-bb__row-image" src="${item.image}" alt="${label}" loading="lazy" width="64" height="64">
              <div class="ys-bb__row-info">
                <span class="ys-bb__row-label">${label}</span>
                <span class="ys-bb__row-size">Mărime ${size}</span>
              </div>
              <div class="ys-bb__stepper">
                <button type="button" class="ys-bb__stepper-btn" data-action="decrement" data-variant-id="${item.variantId}" aria-label="Scade cantitatea pentru ${label}, mărime ${size}">−</button>
                <span class="ys-bb__stepper-qty" aria-hidden="true">${item.quantity}</span>
                <button type="button" class="ys-bb__stepper-btn" data-action="increment" data-variant-id="${item.variantId}" aria-label="Crește cantitatea pentru ${label}, mărime ${size}" ${total >= cap ? 'disabled' : ''}>+</button>
              </div>
              <span class="ys-bb__row-total">${this.formatMoney(item.priceCents * item.quantity)}</span>
            </div>
          `;
        })
        .join('');

      this.zones.rows.innerHTML = html;
    }

    renderUpsell() {
      const tier = Number(this.state.tier);
      const show =
        this.payload.upsell.enabled && tier < 3 && this.totalSelectedQty() === this.capForTier(tier);
      if (!show) {
        this.zones.upsell.innerHTML = '';
        this.zones.upsell.hidden = true;
        return;
      }
      const upsellTier = this.payload.upsell.tiers[String(tier)];
      this.zones.upsell.hidden = false;
      const body = applyTokens(upsellTier.body, {
        price: this.formatMoney(upsellTier.incrementCents),
      });
      this.zones.upsell.innerHTML = `
        <div class="ys-bb__upsell-card">
          <p class="ys-bb__upsell-heading">${escapeHtml(upsellTier.heading)}</p>
          <p class="ys-bb__upsell-body">${escapeHtml(body)}</p>
          <button type="button" class="button button--secondary ys-bb__upsell-btn" data-action="upsell">
            ${escapeHtml(upsellTier.buttonLabel)}
          </button>
        </div>
      `;
    }

    renderButton() {
      const total = this.totalSelectedQty();
      const cap = this.capForTier(this.state.tier);
      const complete = total === cap;
      const tierKey = String(this.state.tier);
      const crossed = this.crossedTotalCents(tierKey);
      const packTotal = this.payload.tiers[tierKey].packTotalCents;

      let label;
      if (this.state.submitting) {
        label = this.payload.copy.buttonLoading;
      } else if (this.state.successFlash) {
        label = this.payload.copy.buttonSuccess;
      } else if (complete) {
        label = this.payload.copy.buttonCompleteByTier[tierKey];
      } else {
        const incompleteTemplate =
          cap === 1 ? this.payload.copy.buttonIncompleteSingular : this.payload.copy.buttonIncomplete;
        label = applyTokens(incompleteTemplate, { selected: total, total: cap });
      }

      const disabled = this.state.submitting || !complete;

      this.zones.button.innerHTML = `
        <button type="button" class="button button--primary ys-bb__atc-btn" data-action="submit" aria-disabled="${disabled}">
          <span class="ys-bb__atc-label">${escapeHtml(label)}</span>
          <span class="ys-bb__atc-totals">
            <span class="ys-bb__atc-price">${this.formatMoney(packTotal)}</span>
            ${crossed != null ? `<s class="ys-bb__atc-compare">${this.formatMoney(crossed)}</s>` : ''}
          </span>
        </button>
      `;
    }

    renderError() {
      this.zones.error.hidden = !this.state.errorMessage;
      this.zones.error.textContent = this.state.errorMessage || '';
    }

    // ---- cart submission ----

    buildCartItems() {
      const map = new Map();
      const order = [];
      this.state.selections.forEach((s) => {
        if (!map.has(s.variantId)) {
          map.set(s.variantId, 0);
          order.push(s.variantId);
        }
        map.set(s.variantId, map.get(s.variantId) + s.quantity);
      });
      return order.map((id) => ({ id, quantity: map.get(id) }));
    }

    onSubmit() {
      if (this.state.submitting || !this.isComplete()) return;

      this.state.submitting = true;
      this.state.errorMessage = null;
      this.render();

      const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items: this.buildCartItems() };

      if (cartEl) {
        body.sections = cartEl.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
        if (typeof cartEl.setActiveElement === 'function') {
          cartEl.setActiveElement(document.activeElement);
        }
      }

      const config = fetchConfig('json');
      config.body = JSON.stringify(body);

      fetch(window.routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.state.submitting = false;
            this.state.errorMessage =
              response.description || response.message || this.payload.copy.errorGeneric;
            this.render();
            return;
          }

          this.state.submitting = false;
          this.state.successFlash = true;
          this.render();

          if (cartEl) {
            // renderContents() only clears .is-empty from the inner .drawer__inner node;
            // the outer <cart-drawer>/<cart-notification> element's own .is-empty class
            // (set server-side when the cart was empty at page load) must be cleared here
            // too, or CSS keeps showing the empty-cart layout even after items are added.
            if (cartEl.classList.contains('is-empty')) cartEl.classList.remove('is-empty');
            cartEl.renderContents(response);
          } else {
            window.location = window.routes.cart_url;
          }

          setTimeout(() => {
            this.state.successFlash = false;
            this.render();
          }, 2500);
        })
        .catch((e) => {
          console.error(e);
          this.state.submitting = false;
          this.state.errorMessage = this.payload.copy.errorGeneric;
          this.render();
        });
    }
  }

  customElements.define('ys-bundle-builder', YsBundleBuilder);
})();
